import { Matrix, Point, Rectangle } from 'pixi.js';
import type { CanvasTextStyleRenderer } from '../renderer/CanvasTextStyle';
import { DEFAULT_TEXT_ALPHA } from '../textMetricsHelpers';
import type { CanvasWordTokenElement } from './CanvasWordTokenElement';
import { measureWord } from './CanvasTextMetrics';

export interface CanvasTextMetricItemAPI {
    text: string;
    style: CanvasTextStyleRenderer;
    ascent: number;
    descent: number;
    fontAscent: number;
    fontDescent: number;
    fontHeight: number;
    width: number;
    height: number;
    bounds: Rectangle;
    baselineY: number;
    ascentY: number;
    descentY: number;
    fontAscentY: number;
    fontDescentY: number;
    globalWordElementIndex: number;
}

export enum AnimatableGeometricWordProperties {
    x = 'x',
    y = 'y'
}

export enum AnimatableNonGeometricWordProperties {
    alpha = 'alpha'
}

/**
 * CanvasWordElementMetrics is a class that represents the measurement of a word element.
 * It stores both the normal and highlight style so that it can draw the requested style when needed.
 * Note though that only one style is used to determine the metrics of the word element. That is determined
 * by getStyleForMetrics and is the style returned by the style property.
 */
export class CanvasWordElementMetrics implements CanvasTextMetricItemAPI {
    #text: string = '';
    #normalStyle: CanvasTextStyleRenderer = null;
    #highlightStyle: CanvasTextStyleRenderer = null;
    #highlightStyleWithColorsFromNormalStyle: CanvasTextStyleRenderer = null;
    #textMetrics: TextMetrics;
    #wordElementToken: CanvasWordTokenElement = null;
    #globalWordElementIndex = -1;
    #isHidden = false;

    // the position of the word element relative to the canvas
    x = 0;
    y = 0;

    constructor(text: string, normalStyle: CanvasTextStyleRenderer, highlightStyle: CanvasTextStyleRenderer, metrics: TextMetrics, wordElementToken: CanvasWordTokenElement) {
        this.#text = text;
        this.#normalStyle = normalStyle;
        this.#highlightStyle = highlightStyle;
        this.#textMetrics = metrics;
        this.#wordElementToken = wordElementToken;
    }

    /** Update the style reference */
    updateStyles(normalStyle: CanvasTextStyleRenderer, highlightStyle: CanvasTextStyleRenderer) {
        this.#normalStyle = normalStyle;
        this.#highlightStyle = highlightStyle;
        this.#highlightStyleWithColorsFromNormalStyle = null;
    }

    hide() {
        this.#isHidden = true;
    }

    show() {
        this.#isHidden = false;
    }

    /** @internal */
    setGlobalWordElementIndex(value: number) {
        this.#globalWordElementIndex = value;
    }

    partialAdvanceWidth(start: number, end: number): number {
        return this.#textMetrics.width * (end - start) / this.#text.length;
    }

    get globalWordElementIndex(): number {
        return this.#globalWordElementIndex;
    }

    get ascentY(): number {
        return this.y - this.ascent;
    }

    get baselineY(): number {
        return this.y;
    }

    get descentY(): number {
        return this.y + this.descent;
    }

    /**
     * Returns the primary style, which is used to determine this element's metrics
     */
    get style(): CanvasTextStyleRenderer {
        return getStyleForMetrics(this.highlighted, this.#normalStyle, this.#highlightStyle);
    }

    get text(): string {
        return this.#text;
    }

    get #metrics(): TextMetrics {
        if (this.#isHidden) {
            return {
                actualBoundingBoxAscent: 0,
                actualBoundingBoxDescent: 0,
                actualBoundingBoxLeft: 0,
                actualBoundingBoxRight: 0,
                width: 0,
                fontBoundingBoxAscent: this.#textMetrics.fontBoundingBoxAscent,
                fontBoundingBoxDescent: this.#textMetrics.fontBoundingBoxDescent,
            } as TextMetrics;
        }
        else {
            return this.#textMetrics;
        }
    }

    get width(): number {
        return this.#metrics.width;
    }

    get ascent(): number {
        return this.#metrics.actualBoundingBoxAscent;
    }

    get descent(): number {
        return this.#metrics.actualBoundingBoxDescent;
    }

    get height(): number {
        return this.ascent + this.descent;
    }

    get fontAscent(): number {
        return this.#metrics.fontBoundingBoxAscent;
    }

    get fontDescent(): number {
        return this.#metrics.fontBoundingBoxDescent;
    }

    get fontHeight(): number {
        return this.fontAscent + this.fontDescent;
    }

    get fontAscentY(): number {
        return this.y - this.fontAscent;
    }

    get fontDescentY(): number {
        return this.y + this.fontDescent;
    }

    get sourceWordReferenceIndex(): number {
        return this.#wordElementToken.sourceWordReferenceIndex;
    }

    get highlighted(): boolean {
        return this.#wordElementToken.highlighted;
    }

    slice(start: number, end: number): CanvasWordTokenElement {
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.text.length);
        return this.#wordElementToken.slice(clampedStart, clampedEnd);
    }

    get bounds() {
        const textBounds = this._textBounds;
        if (!this.style.shadow) {
            return textBounds;
        }
        const shadowBounds = this._shadowBounds;
        return textBounds.enlarge(shadowBounds);
    }

    get _textBounds(): Rectangle {
        return new Rectangle(
            this.x,
            this.y - this.ascent,
            this.#metrics.width,
            this.#metrics.actualBoundingBoxAscent + this.#metrics.actualBoundingBoxDescent
        );
    }

    get _shadowBounds(): Rectangle {
        // apply shadow angle
        const matrix = new Matrix();
        matrix.tx = this.style.shadowDistance;
        matrix.rotate(this.style.shadowAngle);
        const anchor = new Point(0, 0);
        const transformedAnchor = matrix.apply(anchor);

        const shadowBounds = new Rectangle(
            this._textBounds.x + transformedAnchor.x,
            this._textBounds.y + transformedAnchor.y,
            this._textBounds.width,
            this._textBounds.height
        );

        // apply shadow blur
        shadowBounds.pad(this.style.shadowBlur, this.style.shadowBlur);

        return shadowBounds;
    }

    setPosition(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    removeLastCharacter() {
        if (this.#text.length === 0) {
            throw new Error('Cannot remove last character from an empty word element');
        }
        this.#text = this.#text.slice(0, -1);
        this.#textMetrics = measureWord(this.#text, this.style.fontFamily, this.style.fontSize);
    }

    /* The list of properties that can be animated during draw */
    static get animatableProperties() {
        return [
            ...CanvasWordElementMetrics.animatableGeometricProperties,
            ...Object.values(AnimatableNonGeometricWordProperties),
        ];
    }

    /**
    * The list of geometry properties that can be animated during draw. There properties affect the position and shape of
    * the element being drawn. The properties in this list will be remapped from the reference resolution to the output.
    **/
    static get animatableGeometricProperties() {
        return Object.values(AnimatableGeometricWordProperties);
    }

    /**
     * Draw the word element on the canvas context. The word element has both the normal and highlight style so
     * the isHighlightPass parameter is used to determine which style to use. Draw will skip drawing in certain cases:
     * 1. If the word element is highlighted and the style is set to not render highlighted elements.
     * 2. If the word element style has an alpha of 0.
     */
    draw(context: CanvasRenderingContext2D, isHighlightPass = false) {
        if (this.highlighted && !this.#shouldRenderHighlightedText(isHighlightPass)) {
            return;
        }

        // calculate animated value
        let x = this.x;
        let y = this.y;
        let alpha = DEFAULT_TEXT_ALPHA;
        const animationConfigurations = isHighlightPass ? this.#getAnimationConfigurationsforHighlightPass() : this.#getAnimationConfigurationsforNormalPass();
        animationConfigurations.forEach(animation => {
            switch (animation.property) {
                case 'x': x += animation.computedValues[this.globalWordElementIndex]; break;
                case 'y': y += animation.computedValues[this.globalWordElementIndex]; break;
                case 'alpha': alpha = animation.computedValues[this.globalWordElementIndex]; break;
            }
        });

        // only draw text when visible
        if (alpha > 0) {
            const style = isHighlightPass ? this.#getStyleForHighlightPass() : this.#getStyleForNormalPass();
            style.setupContextWithStyle(context, alpha);
            context.save();
            context.fillText(this.text, x, y);
            context.restore();
        }
    }

    /**
     * Based on which render pass is being used, determines if a highlighted word element should be drawn
     */
    #shouldRenderHighlightedText(isHighlightPass: boolean) {
        return isHighlightPass ? this.#highlightStyle.renderHighlighted : this.#normalStyle.renderHighlighted;
    }

    /**
     * For the highlight pass, we always use the highlight style to render.
     */
    #getStyleForHighlightPass() {
        return this.#highlightStyle;
    }

    /**
     * For the normal pass, we use the normal style to render non-highlighted text. For highlighted text, we use the
     * highlight style but depending on the normal style's renderHighlightedColor property, we may use the normal style
     * colors.
     */
    #getStyleForNormalPass() {
        if (!this.highlighted) {
            // For non-highlighted text always use the normal style
            return this.#normalStyle;
        }
        if (this.#normalStyle.renderHighlightedColor) {
            // For highlighted text, if the normal style is set to render highlighted text, use the highlight style
            return this.#highlightStyle;
        }

        // For highlighted text, we use the highlight style but with the colors from the normal style
        if (!this.#highlightStyleWithColorsFromNormalStyle) {
            this.#highlightStyleWithColorsFromNormalStyle = this.#highlightStyle.clone();
            this.#highlightStyleWithColorsFromNormalStyle.fill = this.#normalStyle.fill;
            this.#highlightStyleWithColorsFromNormalStyle.shadowColor = this.#normalStyle.shadowColor;
        }
        return this.#highlightStyleWithColorsFromNormalStyle;
    }

    /**
     * Unfortunately the old TextAsset did not use the normal and highlight styles quite the same way
     * for animations (as compared to the getStyle methods above). So during draw we need to differentiate
     * between animation the rest of the style. So this method returns the animation configuration to use when
     * drawing the highlight pass.
     */
    #getAnimationConfigurationsforHighlightPass() {
        return this.#highlightStyle.animationConfigurations;
    }

    /**
     * Unfortunately the old TextAsset did not use the normal and highlight styles quite the same way
     * for animations (as compared to the getStyle methods above). So during draw we need to differentiate
     * between animation the rest of the style. So this method returns the animation configuration to use when
     * drawing the normal pass.
     */
    #getAnimationConfigurationsforNormalPass() {
        return this.#normalStyle.animationConfigurations;
    }
}


/**
 * Based on highlight info, returns the style an element should use for metrics. This will return the
 * style that should be visible at "steady state". So, for example, if there's a highlight and the highlight style is
 * enabled for highlights, the highlight style will be returned.
 */
export function getStyleForMetrics(isWordElementHighlighted: boolean, normalStyle: CanvasTextStyleRenderer, highlightStyle: CanvasTextStyleRenderer) {
    if (isWordElementHighlighted && highlightStyle.renderHighlighted) {
        // the element is highlighted and the highlight style should render the element
        return highlightStyle;
    }
    return normalStyle;
}
